import {
  Typography,
  Grid,
  useTheme,
  Divider,
  useMediaQuery,
  styled,
  Button,
  Box,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { keyframes } from '@mui/system';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import StyledSpan from '../Common/StyledSpan';
import SplashScreen from '../SignUp/SplashScreen';
import SplashScreenMobile from '../SignUp/SplashScreenMobile';
import {
  CLIENTS_ROUTE,
  COACHES_ROUTE,
  EMAIL_VERIFICATION_ROUTE,
  ONBOARDING_ROUTE,
  SIGNUP_ROUTE,
  FORGOT_PASSWORD_ROUTE,
} from '../../utils/routes';
import { useAppSelector } from '../../redux/hooks';
import { UserType } from '../User/User';
import {
  getSocialSignInURL,
  socialSignInRequest,
  coachLoginRequest,
  getBrandConfigurations,
} from '../../redux/coach/coachSlice';
import { CoachStatus, IdentityProviderEnum } from '../../types/coach';
import { toastStyles } from '../../utils/utils';
import StyledTextField from '../Common/StyledTextField';
import { isValidEmail, passwordRequirements } from '../../utils/userInputUtils';
import Logo from '../Common/Logo';
import { brandTagKey } from '../../config/constants';
import { removeBrandConfigurations } from '../../utils/brandConfigurationsUtils';
import LanguageSwitcher from '../SignUp/LanguageSwitcher';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';
import { logout } from '../../redux/GlobalAction';

const FacebookButton = styled(Button)({
  borderRadius: '15px !important',
  padding: '8px 32px !important',
  background: '#1877F2',
  color: '#FFF',
  fontFamily: 'Helvetica',
  fontSize: '13px',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: 'normal',
  '&:hover': {
    background: '#1877F2',
  },
});

const GoogleButton = styled(Button)({
  borderRadius: '15px !important',
  padding: '8px 32px !important',
  background: '#FFF',
  border: '1px solid #747775',
  color: '#1F1F1F',
  fontFamily: "'Roboto', Arial, sans-serif",
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '20px',
  '&:hover': {
    background: '#FFF',
    border: '1px solid #747775',
  },
});

const slideInSun = keyframes`
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateX(0);
  }
`;

function Login() {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const theme = useTheme();
  const currentYear = new Date().getFullYear();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const isLoggedIn = useAppSelector((state) => state.coach.loggedInStatus);
  const userRole = useAppSelector((state) => state.coach.role);
  const dispatch = useDispatch();
  const redirectUrl = useAppSelector((state) => state.coach.redirectUrl);
  const location = useLocation();

  const fetchCoachLoading = useAppSelector((state) => state.coach.loading);
  const status = useAppSelector((state) => state.coach.coach.status);
  const [isSignUpFail, setIsSignUpFail] = useState(false);

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const loading = useAppSelector((state) => state.coach.loadingLogin);
  const [allValid, setAllValid] = useState(false);
  const email = useAppSelector((state) => state.forgetPassword.email);
  const [username, setUsername] = useState(email || '');
  const { affiliate } = useParams();
  const brandTag = localStorage.getItem(brandTagKey);
  const urlParams = new URLSearchParams(location.search);
  const code = urlParams.get('code');
  const affiliateTag = urlParams.get('state');
  let errorDescription = urlParams.get('error_description');
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  useEffect(() => {
    if (!fetchCoachLoading) {
      dispatch(logout());
    }
  }, []);
  useEffect(() => {
    if (!isLoggedIn || fetchCoachLoading || userRole !== UserType.COACH) return;

    if (status === CoachStatus.PENDING) {
      navigate(EMAIL_VERIFICATION_ROUTE);
    } else if (status === CoachStatus.APPROVED) {
      navigate(ONBOARDING_ROUTE);
    } else if (status === CoachStatus.COMPLETED) {
      if (checkPermissions(UserPermissionType.COACH_MODULE_VIEW_ACTIVE_COACHES, true)) {
        navigate(COACHES_ROUTE);
      } else {
        navigate(CLIENTS_ROUTE);
      }
    }
  }, [isLoggedIn, fetchCoachLoading]);

  useEffect(() => {
    if (affiliate && affiliate !== brandTag) {
      dispatch(getBrandConfigurations({ brandTag: affiliate, navigate }));
    } else if (!affiliate && !affiliateTag) {
      removeBrandConfigurations();
    }
  }, [affiliate]);

  const handlePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const forgetPasswordHandler = () => {
    // handle forget password
    navigate(brandTag ? `${FORGOT_PASSWORD_ROUTE}/${brandTag}` : FORGOT_PASSWORD_ROUTE);
  };

  const handleSignUP = () => {
    navigate(brandTag ? `${SIGNUP_ROUTE}/${brandTag}` : SIGNUP_ROUTE);
  };

  useEffect(() => {
    let isAllValid = passwordRequirements(password, password)
      .filter((i) => i.isLoginRequirement)
      .every((requirement) => requirement.isValid());

    isAllValid = isAllValid && isValidEmail(username);

    setAllValid(isAllValid);
  }, [password, username]);

  useEffect(() => {
    if (code) {
      if (affiliateTag) {
        dispatch(socialSignInRequest({ code, currentLocation: 'login', brandTag: affiliateTag }));
      } else {
        dispatch(socialSignInRequest({ code, currentLocation: 'login' }));
      }
      setIsSignUpFail(false);
    }
    if (errorDescription) {
      setIsSignUpFail(true);
      errorDescription = '';
      navigate(brandTag ? `${SIGNUP_ROUTE}/${brandTag}` : SIGNUP_ROUTE);
    } else {
      setIsSignUpFail(false);
    }
  }, [location]);

  useEffect(() => {
    if (redirectUrl) {
      if (affiliate) {
        window.location.href = `${redirectUrl}&state=${affiliate}`;
      } else {
        window.location.href = redirectUrl;
      }
    }
  }, [redirectUrl]);

  const handleSocialSignInButtonClick = (identityProvider: IdentityProviderEnum) => {
    dispatch(getSocialSignInURL({ identityProvider }));
  };

  return (
    <Grid
      display="flex"
      flexDirection="column"
      alignItems="center"
      minHeight="100vh"
      sx={{
        background: 'linear-gradient(163deg, #FFF 10.13%, #FFF4F0 89.18%)',
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
        height: '100%',
      }}
      justifyContent="space-between"
    >
      <Grid
        mt="36px"
        position="relative"
        display="flex"
        alignItems="center"
        width="100%"
        flexDirection="row-reverse"
      >
        <Grid
          width="100%"
          display="flex"
          justifyContent="center"
          position="absolute"
          sx={{ animation: `${slideInSun} 0.8s ease forwards` }}
        >
          <Logo />
        </Grid>
        {language.split('-')[0] === 'ar' ? (
          <Grid ml={isSmallScreen ? 2 : '140px'} zIndex={1}>
            <LanguageSwitcher />
          </Grid>
        ) : (
          <Grid mr={isSmallScreen ? 2 : '140px'} zIndex={1}>
            <LanguageSwitcher />
          </Grid>
        )}
      </Grid>
      <Grid
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="85vh"
        gap="10px"
        width="100%"
        minWidth={isSmallScreen ? 358 : 373}
      >
        <Grid
          container
          item
          xs={isSmallScreen ? 11 : 3}
          p={3}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap="20px"
          bgcolor="#fff"
          borderRadius="10px"
          boxShadow={theme.borderStyles.cardBorders}
          border="1px solid #FEE4DB"
          minWidth={isSmallScreen ? 358 : 373}
          width="100%"
          zIndex={1}
        >
          <Typography variant="title2Bold" textAlign="center" color="primary">
            {t('auth.signIn')}
          </Typography>
          <Typography variant="bodyBold" textAlign="center" color="primary">
            {t('auth.subtitle')}
          </Typography>
          <Box display="flex" flexDirection="column" gap="24px" width="100%">
            <Grid gap="12px" display="flex" flexDirection="column" width="100%">
              <FacebookButton
                variant="contained"
                fullWidth
                sx={{ textTransform: 'none !important' }}
                startIcon={<img src="/assets/icons/facebook.svg" alt="facebook" />}
                onClick={() => handleSocialSignInButtonClick(IdentityProviderEnum.FACEBOOK)}
                disabled={fetchCoachLoading}
              >
                {t('auth.signInWithFacebook')}
              </FacebookButton>
              <GoogleButton
                variant="outlined"
                fullWidth
                sx={{ textTransform: 'none !important' }}
                startIcon={<img src="/assets/icons/google.svg" alt="google" />}
                onClick={() => handleSocialSignInButtonClick(IdentityProviderEnum.GOOGLE)}
                disabled={fetchCoachLoading}
              >
                {t('auth.signInWithGoogle')}
              </GoogleButton>
            </Grid>
            <Grid width="100%">
              <Divider
                component="div"
                role="presentation"
                sx={{
                  '&::before, &::after': {
                    borderColor: '#7CAEBE',
                    opacity: 0.5,
                  },
                }}
              >
                <Typography
                  variant="button"
                  textAlign="center"
                  color="#7CAEBE"
                  fontWeight={400}
                  textTransform="none"
                >
                  {t('auth.or')}
                </Typography>
              </Divider>
            </Grid>

            <Box display="flex" flexDirection="column" gap="16px">
              <StyledTextField
                variant="outlined"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t('auth.emailAddress')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailRoundedIcon sx={{ color: '#6CA3AC', fontSize: '24px' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <StyledTextField
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.password')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <img src="/assets/icons/security.svg" alt="email" width={24} height={24} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handlePasswordVisibility}
                      >
                        {showPassword ? (
                          <img src="/assets/icons/visibility.svg" alt="visibility" />
                        ) : (
                          <img src="/assets/icons/visibility-off.svg" alt="visibility-off" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Button
              variant="contained"
              sx={{
                background:
                  'var(--Brand-Cadet-Blue, linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%))',
                borderRadius: '15px',
                '&:hover': {
                  background:
                    'var(--Brand-Cadet-Blue, linear-gradient(138deg, #4e7c91 24.11%, #2d4f5f 83.2%))',
                },
              }}
              fullWidth
              onClick={() => {
                dispatch(coachLoginRequest({ username, password }));
              }}
              disabled={!allValid || loading}
            >
              {loading || fetchCoachLoading ? (
                <Box display="flex">
                  <Typography variant="bodyBold" color="inherit">
                    {t('auth.loggingIn')}
                  </Typography>
                </Box>
              ) : (
                <Box display="flex">
                  <Typography variant="bodyBold" color="inherit">
                    {t('auth.login')}
                  </Typography>
                </Box>
              )}
            </Button>
          </Box>
          <Grid item onClick={forgetPasswordHandler} sx={{ cursor: 'pointer' }}>
            <StyledSpan variant="bodyBold">{t('auth.forgotPassword')}</StyledSpan>
          </Grid>
        </Grid>
        <Box
          display="flex"
          flexDirection="row"
          gap={1}
          zIndex={5}
          onClick={handleSignUP}
          sx={{ cursor: 'pointer' }}
        >
          <StyledSpan variant="bodyBold">{t('auth.doNotHaveAccount')}</StyledSpan>
        </Box>
      </Grid>
      <Typography
        variant="caption2Regular"
        textAlign="center"
        color="primary"
        marginBottom="10px"
        zIndex={4}
      >
        {currentYear} ProgressMagic. {t('common.allRightsReserved')}
      </Typography>
      {isSmallScreen ? <SplashScreenMobile /> : <SplashScreen />}
      {isSignUpFail && toast.error(t('auth.toastMessage.coachAlreadyExists'), toastStyles.error)}
    </Grid>
  );
}

export default Login;
